import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Elisama from "../../assets/testemunhos/elisama.png";
import Flavio from "../../assets/testemunhos/flavio.png";
import Rudson from "../../assets/testemunhos/rudson.png";

const TestimonialsSection = () => {
  const testimonials = [
    {
      text: "Transformamos nosso e-commerce em aplicativo e registramos um crescimento de 40% nas vendas em apenas três meses. O processo foi extremamente simples e rápido.",
      name: "Elisama",
      role: "Lojas Íntimos",
      image: Elisama
    },
    {
      text: "Ficamos impressionados com a facilidade. Em poucos dias nosso aplicativo estava publicado e o engajamento dos clientes praticamente triplicou.",
      name: "Rudson", 
      role: "OQFM Turismo",
      image: Rudson
    },
    {
      text: "Conseguimos colocar nossa marca na palma da mão dos clientes sem investir uma fortuna em desenvolvimento. O suporte foi impecável durante todo o processo.",
      name: "Flávio",
      role: "Portal Estado Maior", 
      image: Flavio
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 120 : -120,
      opacity: 0,
      scale: 0.95,
      filter: "blur(4px)"
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      filter: "blur(0px)"
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 120 : -120,
      opacity: 0,
      scale: 0.95,
      filter: "blur(4px)"
    })
  };

  // Drag configurations for touch swipe gestures
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  return (
    <section id="testimonials" className="py-20 md:py-32 bg-white overflow-hidden relative border-t border-gray-50">
      {/* Decorative Blur Backgrounds */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-emerald-50 rounded-full blur-[120px] -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-blue-50 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Histórias de Sucesso
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base md:text-lg font-light leading-relaxed px-2">
            Veja como empresas estão ampliando seus resultados utilizando a CriaApp.
          </p>
        </div>

        {/* Testimonial Slider Area (Responsive heights to completely avoid mobile cutoffs) */}
        <div className="relative min-h-[380px] xs:min-h-[340px] sm:min-h-[280px] md:min-h-[260px] flex items-center justify-center">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.3 },
                filter: { duration: 0.3 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.8}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  handleNext();
                } else if (swipe > swipeConfidenceThreshold) {
                  handlePrev();
                }
              }}
              className="absolute w-full px-2 sm:px-4 cursor-grab active:cursor-grabbing select-none touch-pan-y"
            >
              {/* Card Container */}
              <div className="bg-white rounded-3xl p-5 xs:p-6 sm:p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-gray-100 flex flex-col md:flex-row items-center gap-5 sm:gap-6 md:gap-12 max-w-3xl mx-auto">
                
                {/* Avatar / Portrait */}
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 xs:w-20 xs:h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-gray-50/80 shadow-md">
                    <img 
                      src={testimonials[currentIndex].image} 
                      alt={testimonials[currentIndex].name} 
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>
                  {/* Floating Quotation Sign */}
                  <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-7 h-7 sm:w-10 sm:h-10 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm sm:text-xl shadow-md shadow-emerald-500/20">
                    &ldquo;
                  </div>
                </div>

                {/* Text & Meta info */}
                <div className="flex-1 text-center md:text-left">
                  {/* Gold rating stars */}
                  <div className="flex mb-3 justify-center md:justify-start space-x-0.5">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  {/* Testimonial Quote */}
                  <p className="text-gray-700 text-sm xs:text-base md:text-lg font-light leading-relaxed mb-4 md:mb-6 italic">
                    &ldquo;{testimonials[currentIndex].text}&rdquo;
                  </p>
                  
                  {/* Author Meta */}
                  <div>
                    <h4 className="font-bold text-gray-900 text-base md:text-lg tracking-tight">
                      {testimonials[currentIndex].name}
                    </h4>
                    <p className="text-emerald-600 font-semibold text-[10px] sm:text-xs tracking-wider uppercase mt-0.5">
                      {testimonials[currentIndex].role}
                    </p>
                  </div>
                </div>

              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Dot Indicators & Controls */}
        <div className="flex items-center justify-center gap-6 mt-8 sm:mt-12">
          
          {/* Previous Arrow */}
          <button 
            onClick={handlePrev}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-gray-200 bg-white text-gray-600 flex items-center justify-center hover:bg-gray-50 hover:text-gray-900 hover:scale-105 transition-all shadow-sm focus:outline-none cursor-pointer"
            aria-label="Anterior"
          >
            <FiChevronLeft className="w-5 h-5" />
          </button>
          
          {/* Pagination Indicators */}
          <div className="flex space-x-1.5">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`h-2 rounded-full transition-all duration-300 focus:outline-none cursor-pointer ${
                  currentIndex === index ? 'w-6 bg-emerald-500 shadow-sm shadow-emerald-500/20' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir para depoimento ${index + 1}`}
              />
            ))}
          </div>

          {/* Next Arrow */}
          <button 
            onClick={handleNext}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-gray-200 bg-white text-gray-600 flex items-center justify-center hover:bg-gray-50 hover:text-gray-900 hover:scale-105 transition-all shadow-sm focus:outline-none cursor-pointer"
            aria-label="Próximo"
          >
            <FiChevronRight className="w-5 h-5" />
          </button>

        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;